import React, { PropsWithChildren, useEffect } from 'react';
import { Col, Form, Tab, Tabs } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import InputGroup from 'react-bootstrap/InputGroup';
import { useTranslation } from 'react-i18next';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import i18n from 'i18n';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import _ from 'lodash';
import {
  AuthSettings,
  MockService,
  ProxyConfiguration,
  ResponseDelay,
  ServiceMockLoggingConfig,
} from 'types/rest/mockServiceTypes';
import { AssetAssignmentBundle } from './SslConfiguration';
import InlineHelp from 'components/ui/inlineHelp/InlineHelpProps';
import NoMatchRadioGroup from './Params/NoMatchRadioGroup';
import ThinkTimeRadioGroup from './Params/ThinkTimeRadioGroup';
import ProxyConfig from './Params/ProxyConfig';
import RateLimitsTab from './Params/RateLimitsTab';
import AuthenticationTab from './Params/AuthenticationTab';
import { MsDetailParametersTab } from 'store/mockServices/mockServiceTypes';
import HttpKeystoreOverride from './Params/HttpKeystoreOverride';

interface MockParameterProps {
  state: MockParameterState;
  onUpdateState: (state: MockParameterState, debounce?: boolean) => void;
  isContainerScalable: boolean;
  mockServicesMaxReplicas: number;
  disabled?: boolean;
  assetBundle: AssetAssignmentBundle;
  onCertificatesChanged: (assetAssignment: AssetAssignmentBundle) => void;
  showSslConfig: boolean;
  showRateLimits: boolean;
  showAuthentication?: boolean;
  onParamTabChanged: (tab: MsDetailParametersTab) => void;
  activeTab: MsDetailParametersTab;
}

export interface MockParameterState {
  id: number;
  thinkTime: number;
  liveSystemHost: string;
  liveSystemPort: number;
  replicas: number;
  noMatchingRequestPreference: string;
  responseDelay?: ResponseDelay;
  proxy?: ProxyConfiguration;
  httpRunnerOverrideKeystore?: boolean;
  httpRunnerOverrideTruststore?: boolean;
  loggingConfig?: ServiceMockLoggingConfig;
  rateLimit?: number;
  rateBurst?: number;
  authSettings?: AuthSettings | null;
}

type templateParamField =
  | 'thinkTime'
  | 'noMatchingRequestPreference'
  | 'liveSystemHost'
  | 'replicas'
  | 'responseDelay'
  | 'certificateId'
  | 'proxy';

const MockParameters = (props: MockParameterProps) => {
  const { t } = useTranslation('mockServices');
  const updateState = (field: templateParamField, value: any) => {
    if (props.state[field] !== value) {
      props.onUpdateState({ ...props.state, [field]: value });
    }
  };

  const updateResponseDelay = (value: ResponseDelay) => {
    updateState('responseDelay', value);
  };

  const onNoMatchReqPrefChange = (value: string) => {
    // No change has happened
    if (props.state.noMatchingRequestPreference === value) {
      return;
    }

    const updatedState: any = Object.assign({}, props.state);
    updatedState['noMatchingRequestPreference'] = value;

    // If we choose redirect to a live system, we should provide some default value,
    if (value === 'bypasslive') {
      updatedState['liveSystemHost'] = 'http://';
    } else {
      updatedState['liveSystemHost'] = '';
    }

    props.onUpdateState(updatedState, false);
  };

  const onUpdateThinkTime = (value: any) => {
    if (!_.isNil(value)) {
      updateState('thinkTime', parseInt(value, 10));
    }
  };

  const onUpdateLiveSystemHost = (value: any) => {
    updateState('liveSystemHost', value);
  };

  const onUpdatereplicas = (value: any) => {
    updateState('replicas', parseInt(value, 10));
  };

  /**
   * Use effect for applying changes coming from outside.
   * */
  useEffect(() => {
    if (thinkTimeInput.value !== props.state.thinkTime) {
      thinkTimeInput.resetValue(props.state.thinkTime);
    }

    if (replicasInput.value !== props.state.replicas) {
      replicasInput.resetValue(props.state.replicas);
    }
  }, [props.state.replicas, props.state.thinkTime]);

  const thinkTimeInput = useFormInput(
    props.state.thinkTime,
    (fn: CallbackFn) => {
      if (fn.isValid) {
        if (fn.value !== null && fn.value !== undefined && fn.value.toString().length === 0) {
          fn.value = 0;
        }
        onUpdateThinkTime(fn.value);
      }
    },
    {
      type: ValidatorType.REQUIRED_IGNORE_INIT_CARET,
      invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.required'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.numberValidation'),
    },
    {
      type: ValidatorType.MAX,
      value: 999999999,
      invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.numberValidationRange'),
    },
  );

  const replicasInput = useFormInput(
    props.state.replicas,
    (fn: CallbackFn) => {
      if (fn.isValid) {
        onUpdatereplicas(fn.value);
      }
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorReplicasEmpty'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorReplicasNaN'),
    },
    {
      type: ValidatorType.MAX,
      value: props.mockServicesMaxReplicas,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorReplicasLimitExceeded', {
        limit: props.mockServicesMaxReplicas,
      }),
    },
  );

  const containerReplicasDom = () => {
    return (
      <Row className={'mt-4'}>
        <Col md={3}>
          <h4>Performance</h4>
          <Form.Group>
            <Form.Label htmlFor={'ms-replicas'}>{t('parametersTab.replicas')}</Form.Label>
            <InputGroup className="mb-1">
              <BzFormControlWithHook
                id={'ms-replicas'}
                useFormInputHook={replicasInput}
                placeholder={t('parametersTab.replicasPlaceholder')}
                required={false}
                value={props.state.replicas + ''}
                disabled={props.disabled}
              />
            </InputGroup>
            <InlineHelp>
              Virtual Service will run in multiple instances to offer better performance during request handling.
            </InlineHelp>
          </Form.Group>
        </Col>
      </Row>
    );
  };

  return (
    <div id="mock-parameter-tab">
      <Tabs
        className={'mb-2 tabs-btn-group-design'}
        variant={'pills'}
        transition={false}
        activeKey={props.activeTab}
        onSelect={(item: any) => {
          props.onParamTabChanged(item);
        }}
      >
        {/* Think Time */}
        <Tab className={'thinkTimeTab'} eventKey="THINK_TIME" title={t('parametersTab.thinkTime')}>
          <MockTabParamContent>
            <ThinkTimeRadioGroup
              key={props.state.id}
              responseDelay={props.state.responseDelay}
              updateResponseDelay={updateResponseDelay}
              disabled={props.disabled}
            />
          </MockTabParamContent>
        </Tab>

        {/* No Matching */}
        <Tab className={'noMatchingTab'} eventKey="NO_MATCHING_REQUEST" title={t('parametersTab.noMatchingRequest')}>
          <MockTabParamContent>
            <NoMatchRadioGroup
              id={props.state.id}
              liveSystemHost={props.state.liveSystemHost}
              assetBundle={props.assetBundle}
              onUpdateLiveSystemHost={onUpdateLiveSystemHost}
              noMatchingRequestPreference={props.state.noMatchingRequestPreference}
              onNoMatchReqPrefChange={onNoMatchReqPrefChange}
              disabled={props.disabled}
              onAssetBundleChanged={props.onCertificatesChanged}
            />
          </MockTabParamContent>
        </Tab>

        {/* Proxy */}
        <Tab className={'proxyTab'} eventKey="PROXY" title={t('parametersTab.proxy')}>
          <MockTabParamContent>
            <ProxyConfig
              proxyConfig={props.state.proxy}
              disabled={props.disabled}
              onUpdateProxyConfig={proxyConfig => updateState('proxy', proxyConfig)}
            />
          </MockTabParamContent>
        </Tab>

        {/* SSL Config */}
        {props.showSslConfig === true && (
          <Tab className={'sslAuthTab'} eventKey="SSL_AUTH" title={'Keystore & Truststore Override'}>
            <MockTabParamContent>
              <HttpKeystoreOverride mockService={props.state as MockService} onUpdateState={props.onUpdateState} />
            </MockTabParamContent>
          </Tab>
        )}

        {/* Rate Limits */}
        {props.showRateLimits === true && (
          <Tab className={'rateLimitsTab'} eventKey="RATE_LIMITS" title={t('parametersTab.rateLimits')}>
            <MockTabParamContent>
              <RateLimitsTab
                rateLimit={props.state.rateLimit}
                rateBurst={props.state.rateBurst}
                disabled={props.disabled}
                onUpdateRateLimit={value => props.onUpdateState({ ...props.state, rateLimit: value })}
                onUpdateRateBurst={value => props.onUpdateState({ ...props.state, rateBurst: value })}
              />
            </MockTabParamContent>
          </Tab>
        )}

        {/* Authentication */}
        {props.showAuthentication !== false && (
          <Tab className={'authenticationTab'} eventKey="AUTHENTICATION" title={'Authentication'}>
            <MockTabParamContent>
              <AuthenticationTab
                authSettings={props.state.authSettings}
                disabled={props.disabled}
                onUpdateAuthSettings={value => props.onUpdateState({ ...props.state, authSettings: value })}
              />
            </MockTabParamContent>
          </Tab>
        )}
      </Tabs>

      {/* Number of Instances */}
      {props.isContainerScalable && props.mockServicesMaxReplicas > 1 && containerReplicasDom()}
      {/* ./Number of Instances */}
    </div>
  );
};

const MockTabParamContent = (props: PropsWithChildren<any>) => {
  return (
    <Row className={'pt-4 pl-1'}>
      <Col md={4}>{props.children}</Col>
    </Row>
  );
};

export default MockParameters;
